import { IconId, IconLicense, IconUserShield } from '@tabler/icons-react';
import { RIDES_PROVIDERS, RIDES_VERIFICATION_STATUS, RIDES_VERIFICATION_TYPE, type RidesVerificationView } from '@actschurch/ccms-sdk';
import { ruleCodeCopy, type PillKind } from '@/lib/copy';
import { useCopy } from '@/lib/settings';
import { stampToDate } from '@/lib/format';
import { Pill } from './Pill';
import '@/styles/member.css';

const ICONS: Record<number, typeof IconId> = {
  [RIDES_VERIFICATION_TYPE.GovernmentId]: IconId,
  [RIDES_VERIFICATION_TYPE.DriversLicence]: IconLicense,
  [RIDES_VERIFICATION_TYPE.GuardianId]: IconUserShield,
};

const TITLES: Record<number, string> = {
  [RIDES_VERIFICATION_TYPE.GovernmentId]: 'Your ID',
  [RIDES_VERIFICATION_TYPE.DriversLicence]: "Driver's licence",
  [RIDES_VERIFICATION_TYPE.GuardianId]: "Parent or guardian's ID",
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const TODO_KEYS: Record<number, string> = {
  [RIDES_VERIFICATION_TYPE.GovernmentId]: 'checks.id_todo',
  [RIDES_VERIFICATION_TYPE.DriversLicence]: 'checks.licence_todo',
  [RIDES_VERIFICATION_TYPE.GuardianId]: 'checks.guardian_todo',
};

export interface VerificationPillItem {
  typeId: number;
  verification?: RidesVerificationView | null;
  /** Override the explanation line. */
  sub?: string;
}

/** Plain-language status for one check: pill (words, never colour alone) and one sentence. */
export function useCheckStatus() {
  const { t } = useCopy();
  return (typeId: number, v: RidesVerificationView | null | undefined, providerName?: string | null): { label: string; kind: PillKind | 'neutral'; detail: string } => {
    switch (v?.statusID) {
      case RIDES_VERIFICATION_STATUS.Verified: {
        const on = stampToDate(v.verifiedAt ?? null);
        return { label: 'Checked', kind: 'ok', detail: on ? `Checked on ${on.getDate()} ${MONTHS[on.getMonth()]} ${on.getFullYear()}.` : t('checks.verified') };
      }
      case RIDES_VERIFICATION_STATUS.Pending:
        return {
          label: 'Waiting for a check',
          kind: 'warn',
          detail: (v.providerName ?? providerName) === RIDES_PROVIDERS.Simulated ? 'Being checked now.' : t('checks.pending'),
        };
      case RIDES_VERIFICATION_STATUS.Failed:
        return { label: "Didn't pass", kind: 'bad', detail: v.failureCode ? ruleCodeCopy(v.failureCode) : t('checks.failed') };
      case RIDES_VERIFICATION_STATUS.Expired:
        return { label: 'Expired', kind: 'bad', detail: t('checks.expired') };
      case RIDES_VERIFICATION_STATUS.Revoked:
        return { label: 'Withdrawn', kind: 'bad', detail: t('checks.revoked') };
      default:
        return { label: 'Not done yet', kind: 'neutral', detail: t(TODO_KEYS[typeId] ?? 'checks.id_todo') };
    }
  };
}

/**
 * One row per ID check: icon, name, status pill and a one-line explanation. Only render it once the
 * verifications have loaded: while loading or after an error show a skeleton or an error instead,
 * never a status that might be false.
 */
export function VerificationPills({ items, providerName, variant = 'boxed' }: { items: VerificationPillItem[]; providerName?: string | null; variant?: 'boxed' | 'list' }) {
  const status = useCheckStatus();
  return (
    <div className={`rm-checks${variant === 'boxed' ? ' boxed' : ''}`}>
      {items.map(({ typeId, verification, sub }) => {
        const Icon = ICONS[typeId] ?? IconId;
        const s = status(typeId, verification, providerName);
        return (
          <div className="rm-check" key={typeId}>
            <span className="ic" aria-hidden="true"><Icon size={22} /></span>
            <div className="meta">
              <div className="head">
                <b>{TITLES[typeId] ?? 'Document'}</b>
                <Pill kind={s.kind}>{s.label}</Pill>
              </div>
              <p>{sub ?? s.detail}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
